import { useRef, useState, useEffect, useCallback } from "react";

interface LazyVideoProps {
  src: string;
  className?: string;
  onClick?: () => void;
}

export function LazyVideo({ src, className, onClick }: LazyVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && ref.current) {
      ref.current.load();
    }
  }, [isVisible, src]);

  const handlePointerEnter = useCallback(() => {
    const el = ref.current;
    if (!el || !isVisible) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  }, [isVisible]);

  const handlePointerLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
  }, []);

  // #t=0.1 forces iOS to load and display the frame at 0.1s
  const videoSrc = isVisible ? `${src}#t=0.1` : undefined;

  return (
    <video
      ref={ref}
      src={videoSrc}
      className={className}
      muted
      loop
      playsInline
      preload={isVisible ? "metadata" : "none"}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      style={{ cursor: onClick ? "pointer" : undefined }}
    />
  );
}
